<template>
  <i-modal :title="hasRejectedAssets ? rejectedModalTitle : LANG.fileUpload" v-model:show="visible">
    <form ref='form' @submit.prevent.stop>
      <div class="responsive-table">
        <Loading class="loader" v-if="this.filesLoading"></Loading>
        <div v-else-if="uploadError">
          <span class="error">{{ uploadError }}</span>
        </div>
        <table v-else-if="MAM_ENABLE_INSTANTUPLOAD" class="filelist">
          <colgroup>
            <col width="60px">
            <col width="auto">
            <col width="120px" v-for="(field, idx) in MAM_INSTANTUPLOAD_FIELDS" :key="idx">
            <col width="auto">
            <col width="18px">
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th>{{ LANG.filename }}</th>
              <th v-for="(field, idx) in MAM_INSTANTUPLOAD_FIELDS" :key="idx">{{ field.label }}</th>
              <th>{{ LANG.status }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, idx) in files" :key="idx">
              <th><img :src="file.preview" alt="Icon" width="52"></th>
              <th><input style="width: 100%" class="upload-cell filename i-css-ellipsis" type="text" @change="check(idx, $event)" :value="file.name"></th>
              <th v-for="(field, idx2) in file.additionalFields" :key="idx2">
                <input style="width: 120px" :name="field.fieldname" :required="field.requirement" v-model="field.value"/>
              </th>
              <th>
                <span v-if="status.progress === 100 && file.error" class="upload-cell i-css-ellipsis" v-html="file.status"></span>
                <span v-else class="upload-cell i-css-ellipsis" :class="file.error ? 'error' : ''">{{ file.status.text ? file.status.text : file.status }}
                  <input v-if="file.status.confirm" type="checkbox" v-model="file.overwrite">
                </span>
              </th>
              <th><i class="fas fa-times" name="times" @click="removeFile(idx)"></i></th>
            </tr>
          </tbody>
        </table>
        <table v-else-if="hasRejectedAssets" class="filelist">
          <colgroup>
            <col width="60px">
            <col width="auto">
          </colgroup>
          <thead>
            <tr>
              <th>{{ LANG.filename }}</th>
              <th>{{ LANG.rejectedReason }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, idx) in Object.keys(rejectedAssets)" :key="idx">
              <td>{{ file }}</td>
              <td>{{ rejectedAssets[file].join(' ') }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else>
          <i-progress-bar
            v-if="multistatus.uploading"
            :value="multistatus.progress">
              <strong>{{ `${multistatus.count} ${LANG.of} ${multistatus.length}` }}</strong>
          </i-progress-bar>
        </div>
        <i-progress-bar
          v-if="status.uploading"
          :value="status.progress">
        </i-progress-bar>
      </div>
    </form>

    <template #footer>
      <div>
        <i-button variant="secondary" @click.prevent="cancel">{{ LANG.cancel }}</i-button>
        <i-button v-if="uploadButton" :disabled="!canUpload" variant="primary" @click.prevent="upload">{{ LANG.upload }}</i-button>
        <i-button v-if="hasRejectedAssets" :disabled="!confirmEnabled" variant="primary" @click.prevent="multiUpload">{{ LANG.continue }}</i-button>
      </div>
    </template>
  </i-modal>
</template>

<script>
import { mapState } from 'vuex';
import { mapBrowserActions, mapBrowserMutations } from '@/store';

import Icon from '@/components/lib/icon/Icon.vue';
import Button from '@/components/lib/button/Button.vue';
import Modal from '@/components/lib/modal/Modal.vue';
import ProgressBar from '@/components/lib/progress-bar/ProgressBar.vue';
import globals from '@/globals';

import fetchService from '@/utils/model/fetch_service';
import Loading from '@/components/common/Loading.vue';

const STATUS_MAPPING = {
  FILE_EXISTS: {
    text: 'A file with that name already exists. Overwrite?',
    confirm: true,
  },
  MIME_TYPE_MISMATCH: {
    text: 'MIME Type mismatch',
    confirm: false,
  },
  ALREADY_QUEUED: {
    text: 'Already queued',
    confirm: false,
  },
};

export default {
  components: {
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Modal.name]: Modal,
    [ProgressBar.name]: ProgressBar,
    Loading,
  },
  props: {
    showModal: {
      default: false,
      type: Boolean,
    },
    droppedFiles: null,
    category: {
      default: '',
      type: String,
    },
  },
  data: () => ({
    visible: false,
    info: Object,
    uploadError: '',
    status: {
      uploading: false,
      progress: 0,
      count: 0,
      error: 0,
    },
    multistatus: {
      uploading: false,
      progress: 0,
      count: 0,
      length: 0,
    },
    files: [],
    tempFiles: [],
    chunkSize: 1024 * 1024 * 5,
    filesLoading: true,
    MAM_ENABLE_INSTANTUPLOAD: false,
    MAM_INSTANTUPLOAD_FIELDS: [],
    rejectedAssets: {},
  }),
  computed: {
    ...mapState(['LANG']),
    uploadButton() {
      return globals.I.settings.system.mam_enable_instantupload;
    },
    canUpload() {
      let allowed = true;

      if (this.status.uploading || this.multistatus.uploading) {
        return false;
      }

      if (this.uploadError) {
        return false;
      }

      this.files.forEach((file) => {
        if (file.error && file.status.confirm !== file.overwrite) {
          allowed = false;
        }

        for (let fieldIdx = 0; fieldIdx < file.additionalFields.length; fieldIdx += 1) {
          const field = file.additionalFields[fieldIdx];
          if (field.requirement && !field.value) {
            allowed = false;
          }
        }
      });

      return allowed;
    },
    confirmEnabled() {
      if (this.MAM_ENABLE_INSTANTUPLOAD) {
        return false;
      }

      if (Object.keys(this.files).length) {
        return true;
      }
      return false;
    },
    hasRejectedAssets() {
      return !!Object.keys(this.rejectedAssets).length;
    },
    rejectedModalTitle() {
      return globals.I.gt.__x('Warning {invalid} out {total} media files are not valid', {
        invalid: Object.keys(this.rejectedAssets).length,
        total: Object.keys(this.files).length + Object.keys(this.rejectedAssets).length,
      });
    },
  },
  watch: {
    showModal(v) {
      this.visible = v;

      if (this.visible) {
        this.fileDrop();
      }
    },
    visible(v) {
      this.$emit('update:showModal', v);
    },
  },
  methods: {
    ...mapBrowserActions([
      'openMultiUpload',
    ]),
    ...mapBrowserMutations([
      'setEntityTableDirty',
    ]),
    getFileUrl(file) {
      return URL.createObjectURL(file);
    },
    async fileDrop() {
      Object.assign(this.$data, this.$options.data.apply(this));
      this.visible = true;

      const params = {
        category: this.category,
      };

      const filesArray = [];

      Array.prototype.forEach.call(this.droppedFiles, (element) => {
        // We do this now only to check for error 414
        const fileData = window.btoa(unescape(encodeURIComponent(element.name + element.size + element.lastModified))).replace(/\//g, '_');
        filesArray.push({
          fileData,
          name: element.name,
          size: element.size,
        });
      });

      params.files = JSON.stringify(filesArray);

      const response = await fetchService.GetData('docbrowser_upload_assets_status', params);
      this.filesLoading = false;

      if (response.error) {
        this.uploadError = response.message;
      } else {
        this.MAM_ENABLE_INSTANTUPLOAD = !!response.data.MAM_ENABLE_INSTANTUPLOAD;
        this.MAM_INSTANTUPLOAD_FIELDS = response.data.MAM_INSTANTUPLOAD_FIELDS;
        response.data.files.forEach((element, idx) => {
          this.files.push({
            preview: this.getFileUrl(this.droppedFiles[idx]),
            title: element.title,
            error: !!element.checks.error,
            status: element.checks.error ? STATUS_MAPPING[element.checks.error] : 'Queued',
            data: this.droppedFiles[idx],
            name: this.droppedFiles[idx].name,
            type: element.checks.mime,
            overwrite: false,
            additionalFields: JSON.parse(JSON.stringify(response.data.MAM_INSTANTUPLOAD_FIELDS)),
          });
        });

        await this.checkRejectedAssets();
        if (!this.MAM_ENABLE_INSTANTUPLOAD && !this.hasRejectedAssets && !this.uploadError) {
          this.multiUpload();
        }
      }
    },
    async upload() {
      this.status.uploading = true;
      this.status.error = false;

      for (const file of this.files) { // eslint-disable-line
        if (file.error && file.status.confirm !== file.overwrite) {
          this.status.error = true;
          this.status.uploading = false;
          break;
        }

        const uploadParams = new FormData();

        for (let fieldIdx = 0; fieldIdx < file.additionalFields.length; fieldIdx += 1) {
          const field = file.additionalFields[fieldIdx];
          uploadParams.append(field.fieldname, field.value);
        }

        uploadParams.append('category', this.category);
        uploadParams.append('overwrite', file.overwrite);
        uploadParams.append('__imperia_upload', file.data);
        uploadParams.append('filename', file.name);

        fetch(globals.I.Util.uri('/document/media/upload/upload'), {
          method: 'POST',
          body: uploadParams,
          credentials: 'include',
        }).then((response) => response.json())
          .then((data) => {
            this.updateStatus();

            if (data.error) {
              this.status.error = true;
              file.status = data.error;
              file.error = true;
            } else {
              file.status = data.message;
              file.error = false;
            }

            if (this.status.progress === 100) {
              if (!this.status.error) {
                this.visible = false;
                this.setEntityTableDirty();
              }
            }
          });
      }
    },
    async multiUpload() {
      if (!this.files) return;

      this.multistatus.uploading = true;
      this.multistatus.length = this.files.length;

      await Promise.all(this.files.map(async (file) => {
        await this.uploadTemp(file.data);
        this.multistatus.count += 1;
        this.multistatus.progress = Math.round((this.multistatus.count / this.files.length) * 100);
      }));

      // TODO: replace with $router.push when this is refactored into a proper Modal
      this.uploadError = await this.openMultiUpload({
        catId: this.category,
        tempFiles: JSON.stringify(this.tempFiles),
      });
    },
    async uploadTemp(file) {
      let fileData = file.name + file.size + file.lastModified;
      fileData = window.btoa(unescape(encodeURIComponent(fileData))).replace(/\//g, '_');

      const uploadData = new FormData();
      uploadData.append('__filename', fileData);
      uploadData.append('chunk_size', this.chunkSize);
      uploadData.append('action', 'chunk');

      const response = await fetch(globals.I.Util.uri('/document/media/dnd_upload'), {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        const offset = parseInt(data.offset, 10);

        await this.uploadChunk(fileData, offset, file);
      }
    },
    async uploadChunk(fileData, offset, file) {
      const uploadData = new FormData();
      const blob = file.slice(offset, parseInt(offset, 10) + parseInt(this.chunkSize, 10));

      uploadData.append('__dnd_field', '__temp_data');
      uploadData.append('__filename', fileData);
      uploadData.append('__offset', offset);
      uploadData.append('__temp_data', blob);
      uploadData.append('action', 'save');

      const response = await fetch(globals.I.Util.uri('/document/media/dnd_upload'), {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        const offset2 = offset + parseInt(this.chunkSize, 10);

        if (offset2 < file.size) {
          await this.uploadChunk(fileData, offset2, file);
        } else {
          this.tempFiles.push({
            filename: `/site/temp/mam/${fileData}`,
            original: file.name,
            origFilesize: file.size,
          });
        }
      }
    },
    async check(index, event) {
      const file = this.files[index];

      const queue = [];

      this.files.forEach((f) => {
        if (f !== file) {
          queue.push(f.name);
        }
      });

      const fileParam = {
        name: file.name,
        user: event.target.value,
        type: file.type,
      };

      const params = {
        category: this.category,
        file: JSON.stringify(fileParam),
        queue: JSON.stringify(queue),
      };

      const response = await fetchService.GetData('docbrowser_upload_assets_check', params);

      file.error = !!response.data.checks.error;
      file.status = response.data.checks.error ? STATUS_MAPPING[response.data.checks.error] : 'Queued';
      file.name = event.target.value;
    },
    removeFile(index) {
      this.files.splice(index, 1);

      if (this.files.length < 1) {
        this.visible = false;
      }
    },
    updateStatus() {
      const maxCount = this.files.length;

      this.status.count += 1;
      this.status.progress = Math.round((this.status.count / maxCount) * 100);
    },
    toBytes(size, type) {
      const types = ['B', 'KB', 'MB', 'GB', 'TB'];
      const key = types.indexOf(type.toUpperCase());

      return (typeof key !== 'boolean') ? size * 1024 ** key : 0;
    },
    async checkRejectedAssets() {
      this.rejectedAssets = {};
      const uploadRestrictionsResponse = await fetchService.GetData('docbrowser_upload_restrictions', { cat_id: this.category });
      if (uploadRestrictionsResponse.error) {
        this.uploadError = uploadRestrictionsResponse.message;
        return;
      }

      const uploadRestrictions = JSON.parse(uploadRestrictionsResponse.data);
      if (!Object.keys(uploadRestrictions).length || uploadRestrictionsResponse.error) {
        return;
      }

      this.files.forEach((file, idx) => {
        let rejected = 0;
        this.rejectedAssets[file.data.name] = [];
        if (uploadRestrictions.allowed_filetypes.length) {
          if (!uploadRestrictions.allowed_filetypes.includes(file.data.type)) {
            this.rejectedAssets[file.data.name].push(`Filetype(${file.data.type}) not allowed.`);
            rejected = 1;
          }
        }
        if (this.toBytes(uploadRestrictions.max_size, 'KB') <= this.toBytes(file.data.size, 'B')) {
          this.rejectedAssets[file.data.name].push(`File is too large. Maximum file size: ${uploadRestrictions.max_size}`);
          rejected = 1;
        } else if (this.toBytes(uploadRestrictions.min_size, 'KB') >= this.toBytes(file.data.size, 'B')) {
          this.rejectedAssets[file.data.name].push(`File is too small. Minumum file size: ${uploadRestrictions.min_size}`);
          rejected = 1;
        }

        if (rejected) {
          delete this.files[idx];
        } else {
          delete this.rejectedAssets[file.data.name];
        }
      });
    },
    cancel() {
      this.visible = false;
    },
  },
};
</script>

<style scoped lang='scss'>
.loader {
  margin: 0 auto;
}

.error {
  color: $red;
}

.filelist {
  min-width: 100%;
  th, td {
    white-space: nowrap;
  }
  th {
    font-weight: bold;
    font-size: 13px;
    font-family: $font-family-regular;
  }
}
.upload-cell {
  display: block;
  max-width: 400px;
}

.progress-bar {
  color: $blue;
}
</style>
